import { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Filter, Grid3X3, LayoutGrid, ZoomIn } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Layout } from '../components/layout/Layout';
import { publicApi } from '../lib/api';
import { cn } from '../lib/utils';

const defaultCategories = [
  'All', 'Destinations', 'Tours', 'Hotels', 'Experiences', 'Nature', 'Culture'
];

const fallbackImages = [
  { id: '1', url: 'https://images.pexels.com/photos/1271619/pexels-photo-1271619.jpeg?auto=compress&cs=tinysrgb&w=800', title: 'Maldives Paradise', category: 'Destinations' },
  { id: '2', url: 'https://images.pexels.com/photos/2356045/pexels-photo-2356045.jpeg?auto=compress&cs=tinysrgb&w=800', title: 'Swiss Alps', category: 'Destinations' },
  { id: '3', url: 'https://images.pexels.com/photos/1592461/pexels-photo-1592461.jpeg?auto=compress&cs=tinysrgb&w=800', title: 'Bali Temple', category: 'Culture' },
  { id: '4', url: 'https://images.pexels.com/photos/2070033/pexels-photo-2070033.jpeg?auto=compress&cs=tinysrgb&w=800', title: 'Taj Mahal', category: 'Culture' },
  { id: '5', url: 'https://images.pexels.com/photos/1320684/pexels-photo-1320684.jpeg?auto=compress&cs=tinysrgb&w=800', title: 'Beach Resort', category: 'Hotels' },
  { id: '6', url: 'https://images.pexels.com/photos/2166559/pexels-photo-2166559.jpeg?auto=compress&cs=tinysrgb&w=800', title: 'Rice Terraces', category: 'Nature' },
  { id: '7', url: 'https://images.pexels.com/photos/1591373/pexels-photo-1591373.jpeg?auto=compress&cs=tinysrgb&w=800', title: 'Mountain Trek', category: 'Experiences' },
  { id: '8', url: 'https://images.pexels.com/photos/2245436/pexels-photo-2245436.png?auto=compress&cs=tinysrgb&w=800', title: 'Desert Safari', category: 'Experiences' },
  { id: '9', url: 'https://images.pexels.com/photos/962464/pexels-photo-962464.jpeg?auto=compress&cs=tinysrgb&w=800', title: 'Kerala Backwaters', category: 'Tours' },
  { id: '10', url: 'https://images.pexels.com/photos/1534411/pexels-photo-1534411.jpeg?auto=compress&cs=tinysrgb&w=800', title: 'Dubai Skyline', category: 'Destinations' },
  { id: '11', url: 'https://images.pexels.com/photos/2325446/pexels-photo-2325446.jpeg?auto=compress&cs=tinysrgb&w=800', title: 'Tropical Beach', category: 'Nature' },
  { id: '12', url: 'https://images.pexels.com/photos/1024960/pexels-photo-1024960.jpeg?auto=compress&cs=tinysrgb&w=800', title: 'Romantic Sunset', category: 'Experiences' },
];

export default function GalleryPage() {
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState(defaultCategories);
  const [activeCategory, setActiveCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const loadGallery = useCallback(async () => {
    try {
      setLoading(true);
      const response = await publicApi.getGallery();
      const data = response.data?.data || response.data || [];
      
      if (data.length > 0) {
        setImages(data);
        // Extract unique categories
        const uniqueCats = ['All', ...new Set(data.map(img => img.category).filter(Boolean))];
        setCategories(uniqueCats);
      } else {
        setImages(fallbackImages);
      }
    } catch (error) {
      console.error('Error loading gallery:', error);
      setImages(fallbackImages);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGallery();
  }, [loadGallery]);

  const filteredImages = activeCategory === 'All' 
    ? images 
    : images.filter(img => img.category === activeCategory);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const goToNext = () => {
    setLightboxIndex((prev) => (prev + 1) % filteredImages.length);
  };

  const goToPrev = () => {
    setLightboxIndex((prev) => (prev - 1 + filteredImages.length) % filteredImages.length);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowRight') goToNext();
    if (e.key === 'ArrowLeft') goToPrev();
  };

  // Masonry layout sizes
  const getSizeClass = (index) => {
    const pattern = [1, 2, 1, 1, 2, 1, 1, 1, 2, 1, 1, 1];
    return pattern[index % pattern.length] === 2 ? 'col-span-2 row-span-2' : 'col-span-1 row-span-1';
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-[#004E64] to-[#006B87] py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-48 h-48 bg-[#FF6B35] rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <span className="inline-block text-[#FF6B35] text-sm font-semibold tracking-wider uppercase mb-4">
            📸 Photo Gallery
          </span>
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Travel Moments
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Explore stunning photographs from our tours and destinations. 
            Get inspired for your next adventure!
          </p>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="sticky top-16 z-20 bg-white/95 backdrop-blur-sm border-b border-gray-100 py-4">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 overflow-x-auto pb-2 scrollbar-hide">
            <div className="flex items-center gap-2 text-[#4A4A4A] mr-2">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium whitespace-nowrap">Filter:</span>
            </div>
            {categories.map((category) => (
              <Button
                key={category}
                variant={activeCategory === category ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveCategory(category)}
                className={cn(
                  'rounded-full whitespace-nowrap',
                  activeCategory === category 
                    ? 'bg-[#FF6B35] hover:bg-[#E85A2D] text-white' 
                    : 'border-gray-200 hover:border-[#FF6B35] hover:text-[#FF6B35]'
                )}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-12 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-4">
          {/* Results Count */}
          <p className="text-[#4A4A4A] mb-6">
            Showing <span className="font-semibold text-[#1A1A1A]">{filteredImages.length}</span> photos
            {activeCategory !== 'All' && <> in <Badge variant="secondary">{activeCategory}</Badge></>}
          </p>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[200px]">
              {[...Array(12)].map((_, i) => (
                <Skeleton key={i} className={cn('rounded-2xl', getSizeClass(i))} />
              ))}
            </div>
          ) : filteredImages.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[200px]">
              {filteredImages.map((image, index) => (
                <div
                  key={image.id}
                  className={cn(
                    'relative group rounded-2xl overflow-hidden cursor-pointer',
                    getSizeClass(index)
                  )}
                  onClick={() => openLightbox(index)}
                >
                  <img
                    src={image.url}
                    alt={image.title || 'Gallery image'}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity text-center text-white p-4">
                      <ZoomIn className="w-8 h-8 mx-auto mb-2" />
                      {image.title && (
                        <p className="font-semibold text-lg">{image.title}</p>
                      )}
                      {image.category && (
                        <Badge className="mt-2 bg-[#FF6B35]">{image.category}</Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-white rounded-2xl">
              <Grid3X3 className="w-16 h-16 text-[#4A4A4A] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#1A1A1A] mb-2">No photos found</h3>
              <p className="text-[#4A4A4A]">Try selecting a different category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && filteredImages[lightboxIndex] && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors z-10"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Navigation */}
          <button
            onClick={(e) => { e.stopPropagation(); goToPrev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors z-10"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors z-10"
          >
            <ChevronRight className="w-8 h-8" />
          </button>

          {/* Image */}
          <div className="max-w-5xl max-h-[80vh] px-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={filteredImages[lightboxIndex].url}
              alt={filteredImages[lightboxIndex].title || 'Gallery image'}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            {filteredImages[lightboxIndex].title && (
              <div className="text-center mt-4">
                <p className="text-white text-xl font-semibold">
                  {filteredImages[lightboxIndex].title}
                </p>
                {filteredImages[lightboxIndex].description && (
                  <p className="text-white/70 mt-1">
                    {filteredImages[lightboxIndex].description}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Thumbnails */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 px-4 overflow-x-auto max-w-full">
            {filteredImages.slice(0, 10).map((img, index) => (
              <button
                key={img.id}
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(index); }}
                className={cn(
                  'flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all',
                  lightboxIndex === index
                    ? 'ring-2 ring-[#FF6B35] opacity-100'
                    : 'opacity-50 hover:opacity-80'
                )}
              >
                <img src={img.url} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>

          {/* Counter */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white text-lg">
            {lightboxIndex + 1} / {filteredImages.length}
          </div>
        </div>
      )}
    </Layout>
  );
}
